import { useMemo, useState, useRef } from "react";
import { Tooltip } from "antd";
import { ChevronRight } from "lucide-react";
import { usePermission } from "app/hooks/usePermission";

/* ── Quick Actions Section ────────────────────────────── */

function QuickActionsSection({ actions }) {
  return (
    <div style={{ padding: "0 10px", flexShrink: 0 }}>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          padding: "12px 6px 4px",
        }}
      >
        Thao tác nhanh
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {actions.map((action) => (
          <QuickActionItem key={action.key} action={action} />
        ))}
      </div>
      <div
        style={{
          height: 1,
          background: "#E5E7EB",
          margin: "10px 0 2px",
        }}
      />
    </div>
  );
}

function QuickActionItem({ action, collapsed }) {
  const [hover, setHover] = useState(false);
  const color = action.color || "#16a34a";

  return (
    <button
      type="button"
      onClick={action.onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={collapsed ? action.label : undefined}
      style={{
        all: "unset",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        height: collapsed ? 40 : "auto",
        padding: collapsed ? "0" : "1px 12px",
        borderRadius: 12,
        cursor: "pointer",
        background: hover ? `${color}15` : `${color}08`,
        border: hover ? `1px solid ${color}66` : `1px solid ${color}33`,
        justifyContent: collapsed ? "center" : "flex-start",
        transition: "all 0.15s ease",
      }}
    >
      {/* Icon */}
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: hover ? `${color}25` : `${color}15`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: color,
          flexShrink: 0,
          transition: "background 0.15s ease",
        }}
      >
        {action.icon}
      </span>

      {/* Label + chevron */}
      {!collapsed && (
        <>
          <span
            style={{
              flex: 1,
              fontSize: 14.5,
              fontWeight: 600,
              color: color,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {action.label}
          </span>
          <ChevronRight
            size={15}
            strokeWidth={2.5}
            style={{ color: color, opacity: 0.8, flexShrink: 0 }}
          />
        </>
      )}
    </button>
  );
}

export const SIDEBAR_ICON_SIZE = 18;
export const SIDEBAR_ICON_STROKE = 1.75;

/* ── Palette (light sidebar) ──────────────────────────── */
const P = {
  text: "#374151",
  textMuted: "#9CA3AF",
  textActive: "#FFFFFF",
  hoverBg: "rgba(0,0,0,0.045)",
  activeBg: process.env.REACT_APP_BACKGROUND_COLOR || "#295e18",
  activeBorder: process.env.REACT_APP_BACKGROUND_COLOR || "#295e18",
  iconActive: "#FFFFFF",
  divider: "rgba(0,0,0,0.08)",
  qaIconBg: "rgba(22,163,74,0.10)",
  logoutHover: "rgba(239,68,68,0.08)",
  logoutColor: "#EF4444",
};

/* ── Helpers ──────────────────────────────────────────── */
function isItemActive(currentPath, itemKey) {
  if (itemKey === "/") return currentPath === "/";
  return currentPath === itemKey || currentPath.startsWith(itemKey + "/");
}

/* ── Badge chip ───────────────────────────────────────── */
function BadgeChip({ value, tooltip }) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const handleTouchStart = () => {
    timerRef.current = setTimeout(() => {
      setOpen(true);
    }, 400);
  };

  const handleTouchEnd = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOpen(false);
  };

  const handleMouseEnter = () => setOpen(true);
  const handleMouseLeave = () => setOpen(false);

  if (typeof value === "number") {
    const badgeEl = (
      <span
        onTouchStart={tooltip ? handleTouchStart : undefined}
        onTouchEnd={tooltip ? handleTouchEnd : undefined}
        onTouchCancel={tooltip ? handleTouchEnd : undefined}
        onMouseEnter={tooltip ? handleMouseEnter : undefined}
        onMouseLeave={tooltip ? handleMouseLeave : undefined}
        onContextMenu={
          tooltip
            ? (e) => {
                if (open) e.preventDefault();
              }
            : undefined
        }
        style={{
          fontSize: 9,
          fontWeight: 700,
          padding: "1px 6px",
          borderRadius: 999,
          background: "#EF4444",
          color: "#fff",
          minWidth: 18,
          textAlign: "center",
          lineHeight: 1.5,
          flexShrink: 0,
        }}
      >
        {value > 99 ? "99+" : value}
      </span>
    );
    return tooltip ? (
      <Tooltip title={tooltip} open={open}>
        {badgeEl}
      </Tooltip>
    ) : (
      badgeEl
    );
  }
  if (value === "NEW") {
    return (
      <span
        style={{
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: 0.5,
          padding: "2px 6px",
          borderRadius: 999,
          background: "rgba(37,99,235,0.28)",
          color: "#93C5FD",
          border: "1px solid rgba(37,99,235,0.45)",
          textTransform: "uppercase",
          lineHeight: 1.2,
          flexShrink: 0,
        }}
      >
        NEW
      </span>
    );
  }
  /* V2 */
  return (
    <span
      style={{
        fontSize: 8,
        fontWeight: 700,
        letterSpacing: 0.5,
        padding: "2px 6px",
        borderRadius: 999,
        background: "rgba(37,99,235,0.18)",
        color: "#93C5FD",
        border: "1px solid rgba(37,99,235,0.35)",
        textTransform: "uppercase",
        lineHeight: 1.2,
        flexShrink: 0,
      }}
    >
      V2
    </span>
  );
}

/* ── Menu item (leaf) ─────────────────────────────────── */
function MenuItem({ item, active, collapsed, onSelect }) {
  const [hover, setHover] = useState(false);

  const bg = active ? P.activeBg : hover ? P.hoverBg : "transparent";

  return (
    <button
      type="button"
      onClick={() => onSelect(item.key)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={collapsed ? item.label : undefined}
      aria-current={active ? "page" : undefined}
      style={{
        all: "unset",
        boxSizing: "border-box",
        position: "relative",
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: collapsed ? "0" : "0 12px",
        height: 40,
        borderRadius: 10,
        cursor: "pointer",
        background: bg,
        border: "1px solid transparent",
        color: active ? P.textActive : P.text,
        fontSize: 13.5,
        fontWeight: active ? 600 : 400,
        transition: "background 0.16s ease, color 0.16s ease",
        justifyContent: collapsed ? "center" : "flex-start",
      }}
    >
      {/* Icon wrapper với background */}
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background 0.16s ease, color 0.16s ease",
          background: active ? "rgba(255,255,255,0.20)" : "rgba(0,0,0,0.06)",
          color: active
            ? "#FFFFFF"
            : (item.color ??
              (process.env.REACT_APP_BACKGROUND_COLOR || "#295e18")),
        }}
      >
        {item.icon}
      </span>

      {/* Label + badge */}
      {!collapsed && (
        <>
          <span
            style={{
              flex: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              fontSize: 15,
              textOverflow: "ellipsis",
              lineHeight: 1,
              fontWeight: 500,
            }}
          >
            {item.label}
          </span>
          {item.badge !== undefined && (
            <BadgeChip value={item.badge} tooltip={item.tooltip} />
          )}
        </>
      )}
      {collapsed && item.badge !== undefined && (
        <span
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            transform: "scale(0.86)",
            transformOrigin: "top right",
          }}
        >
          <BadgeChip value={item.badge} tooltip={item.tooltip} />
        </span>
      )}
    </button>
  );
}

/* ── Group section ────────────────────────────────────── */
function MenuGroup({ group, open, activeKey, onToggle, onSelect, isFirst }) {
  return (
    <div style={{ padding: "0 10px", marginTop: isFirst ? 6 : 0 }}>
      {/* Group label */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          all: "unset",
          boxSizing: "border-box",
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "12px 6px 4px",
          cursor: "pointer",
          color: "#4d5055ff",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          userSelect: "none",
        }}
      >
        <span style={{ flex: 1 }}>{group.label}</span>
      </button>

      {/* Items */}
      <div
        style={{
          maxHeight: open ? group.items.length * 50 : 0,
          overflow: "hidden",
          transition: "max-height 0.22s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            paddingBottom: 4,
          }}
        >
          {group.items.map((item) => (
            <MenuItem
              key={item.key}
              item={item}
              active={isItemActive(activeKey, item.key)}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Top-level export ─────────────────────────────────── */
export function SidebarMenu({
  groups,
  collapsed,
  activeKey,
  onSelect,
  quickActions = [],
}) {
  const { hasActionPermission } = usePermission();
  const allKeys = useMemo(() => groups.map((g) => g.key), [groups]);
  const [openGroups, setOpenGroups] = useState(() => new Set(allKeys));
  const permittedQuickActions = quickActions.filter((action) => {
    if (!action.requiredAction) return true;
    return hasActionPermission(
      action.requiredAction.menuCode,
      action.requiredAction.actionCode,
    );
  });

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  /* ── Collapsed: icon-only flat list ── */
  if (collapsed) {
    const flatItems = groups.flatMap((g) => g.items);
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <nav
          style={{
            padding: "8px 10px",
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
          aria-label="Menu chính"
        >
          {permittedQuickActions.length > 0 && (
            <>
              {permittedQuickActions.map((qa) => (
                <QuickActionItem key={qa.key} action={qa} collapsed />
              ))}
              <div
                style={{
                  height: 1,
                  background: "rgba(0,0,0,0.08)",
                  margin: "6px 4px",
                }}
              />
            </>
          )}
          {flatItems.map((item) => (
            <MenuItem
              key={item.key}
              item={item}
              active={isItemActive(activeKey, item.key)}
              collapsed
              onSelect={onSelect}
            />
          ))}
        </nav>
      </div>
    );
  }

  /* ── Expanded: grouped sections ── */
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      {/* Quick Actions */}
      {permittedQuickActions.length > 0 && (
        <QuickActionsSection actions={permittedQuickActions} />
      )}

      {/* Scrollable menu groups */}
      <nav
        style={{ flex: 1, overflowY: "auto", padding: "4px 0 8px" }}
        aria-label="Menu chính"
      >
        {groups.map((group, idx) => (
          <MenuGroup
            key={group.key}
            group={group}
            open={openGroups.has(group.key)}
            activeKey={activeKey}
            onToggle={() => toggleGroup(group.key)}
            onSelect={onSelect}
            isFirst={idx === 0}
          />
        ))}
      </nav>
    </div>
  );
}
